/**
 * Custom AI review rules (ADR-0034): the organization's rules, a
 * repository's own, the rules in effect for a repository, a repository's
 * override of an organization rule, and the dry run of a draft.
 *
 * Split from `aireview.ts` for length and re-exported from `operations.ts`,
 * as the other phases are. One function per `/v1` operation, paths checked
 * against the generated contract.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type AIReviewRule = Schemas['AIReviewRule'];
export type AIReviewRuleWrite = Schemas['AIReviewRuleWrite'];
export type AIReviewRuleKind = Schemas['AIReviewRuleKind'];
export type AIReviewRuleSide = Schemas['AIReviewRuleSide'];
export type AIReviewRuleSource = Schemas['AIReviewRuleSource'];
export type AIReviewRuleVocabulary = Schemas['AIReviewRuleVocabulary'];
export type AIReviewRuleList = Schemas['AIReviewRuleList'];
export type AIReviewRulesInEffect = Schemas['AIReviewRulesInEffect'];
export type AIReviewRuleOverride = Schemas['AIReviewRuleOverride'];
export type AIReviewRulePreview = Schemas['AIReviewRulePreview'];

interface RepoScope {
  orgId: string;
  repoId: string;
}

const orgPath = (orgId: string) => ({ org_id: orgId });
const orgRulePath = (orgId: string, ruleId: string) => ({ org_id: orgId, rule_id: ruleId });
const repoPath = ({ orgId, repoId }: RepoScope) => ({ org_id: orgId, repo_id: repoId });
const repoRulePath = ({ orgId, repoId }: RepoScope, ruleId: string) => ({
  org_id: orgId,
  repo_id: repoId,
  rule_id: ruleId,
});

export function listOrganizationAIReviewRules(orgId: string): Promise<AIReviewRuleList> {
  return expect(
    http.GET('/organizations/{org_id}/ai-review-rules', { params: { path: orgPath(orgId) } }),
  );
}

export function createOrganizationAIReviewRule(
  orgId: string,
  body: AIReviewRuleWrite,
): Promise<AIReviewRule> {
  return expect(
    http.POST('/organizations/{org_id}/ai-review-rules', {
      params: { path: orgPath(orgId) },
      body,
    }),
  );
}

export function getOrganizationAIReviewRule(orgId: string, ruleId: string): Promise<AIReviewRule> {
  return expect(
    http.GET('/organizations/{org_id}/ai-review-rules/{rule_id}', {
      params: { path: orgRulePath(orgId, ruleId) },
    }),
  );
}

export function updateOrganizationAIReviewRule(
  orgId: string,
  ruleId: string,
  body: AIReviewRuleWrite,
): Promise<AIReviewRule> {
  return expect(
    http.PUT('/organizations/{org_id}/ai-review-rules/{rule_id}', {
      params: { path: orgRulePath(orgId, ruleId) },
      body,
    }),
  );
}

export function deleteOrganizationAIReviewRule(orgId: string, ruleId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/ai-review-rules/{rule_id}', {
      params: { path: orgRulePath(orgId, ruleId) },
    }),
  );
}

export function getRepositoryAIReviewRules(scope: RepoScope): Promise<AIReviewRulesInEffect> {
  return expect(
    http.GET('/organizations/{org_id}/repositories/{repo_id}/ai-review-rules', {
      params: { path: repoPath(scope) },
    }),
  );
}

export function createRepositoryAIReviewRule(
  scope: RepoScope,
  body: AIReviewRuleWrite,
): Promise<AIReviewRule> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/ai-review-rules', {
      params: { path: repoPath(scope) },
      body,
    }),
  );
}

export function updateRepositoryAIReviewRule(
  scope: RepoScope,
  ruleId: string,
  body: AIReviewRuleWrite,
): Promise<AIReviewRule> {
  return expect(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/ai-review-rules/{rule_id}', {
      params: { path: repoRulePath(scope, ruleId) },
      body,
    }),
  );
}

export function deleteRepositoryAIReviewRule(scope: RepoScope, ruleId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/repositories/{repo_id}/ai-review-rules/{rule_id}', {
      params: { path: repoRulePath(scope, ruleId) },
    }),
  );
}

export function disableOrganizationAIReviewRuleForRepository(
  scope: RepoScope,
  ruleId: string,
  reason: string,
): Promise<AIReviewRuleOverride> {
  return expect(
    http.PUT('/organizations/{org_id}/repositories/{repo_id}/ai-review-rules/{rule_id}/override', {
      params: { path: repoRulePath(scope, ruleId) },
      body: { reason },
    }),
  );
}

export function enableOrganizationAIReviewRuleForRepository(
  scope: RepoScope,
  ruleId: string,
): Promise<void> {
  return expectNoContent(
    http.DELETE(
      '/organizations/{org_id}/repositories/{repo_id}/ai-review-rules/{rule_id}/override',
      { params: { path: repoRulePath(scope, ruleId) } },
    ),
  );
}

export function previewRepositoryAIReviewRule(
  scope: RepoScope,
  pullRequestNumber: number,
  rule: AIReviewRuleWrite,
): Promise<AIReviewRulePreview> {
  return expect(
    http.POST('/organizations/{org_id}/repositories/{repo_id}/ai-review-rules/preview', {
      params: { path: repoPath(scope) },
      body: { pull_request_number: pullRequestNumber, rule },
    }),
  );
}
